import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, Box, IconButton, Link, Typography } from "@mui/material";
import React, { useState } from "react";

interface SubscriptionExpiryBannerProps {
  expiryDate: string | null;
  accountStatus?: string;
  thresholdHours?: number;
  supportEmail?: string;
}

const getRemainingTime = (
  expiryDate: string | null,
  accountStatus?: string,
): { hours: number; days: number; show: boolean } | null => {
  if (!expiryDate) {
    return null;
  }

  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffMs = expiry.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours <= 0) {
    return null;
  }

  // For trial users: always show if expiry date exists
  // For full access users: only show if less than 1 week (168 hours)
  const isTrial = accountStatus === "TRIAL";
  const oneWeekInHours = 168;

  if (!isTrial && diffHours > oneWeekInHours) {
    return null;
  }

  const days = Math.floor(diffHours / 24);
  const hours = Math.floor(diffHours % 24);

  return {
    hours: Math.floor(diffHours),
    days,
    show: true,
  };
};

const SubscriptionExpiryBanner: React.FC<SubscriptionExpiryBannerProps> = ({
  expiryDate,
  accountStatus,
  thresholdHours = 48,
  supportEmail = "support@alpha-affinity.com",
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const timeRemaining = getRemainingTime(expiryDate, accountStatus);

  if (!timeRemaining || !isOpen) {
    return null;
  }

  const getTimeMessage = () => {
    const totalHours = timeRemaining.hours;
    const days = timeRemaining.days;
    const remainderHours = Math.floor(totalHours % 24);

    // If >= 48 hours (2 days), show only days (rounded up if 12+ hours)
    if (totalHours >= 48) {
      const roundedDays = remainderHours >= 12 ? days + 1 : days;
      return `${roundedDays} day${roundedDays !== 1 ? "s" : ""}`;
    }

    // If < 48 hours, show hours only
    return `${totalHours} hour${totalHours !== 1 ? "s" : ""}`;
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Alert
        severity="info"
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={handleClose}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
        sx={{
          borderRadius: 0,
          "& .MuiAlert-message": {
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{ textAlign: "center" }}
        >
          Your {accountStatus === "TRIAL" ? "trial" : "subscription"} expires in{" "}
          <strong>{getTimeMessage()}</strong>. Contact{" "}
          <Link
            href={`mailto:${supportEmail}`}
            sx={{
              color: "inherit",
              fontWeight: 600,
              textDecoration: "underline",
              "&:hover": {
                textDecoration: "none",
              },
            }}
          >
            {supportEmail}
          </Link>{" "}
          {accountStatus === "TRIAL"
            ? "to get full access"
            : "to renew your subscription"}
          .
        </Typography>
      </Alert>
    </Box>
  );
};

export default SubscriptionExpiryBanner;
